import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown"
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight"
import Box from "@mui/material/Box"
import ListItem from "@mui/material/ListItem"
import ListItemText from "@mui/material/ListItemText"
import { useTheme } from "@mui/material/styles"
import { useEffect, useState } from "react"
import InfoTooltip from "./InfoTooltip"

interface Props {
    expanded: boolean
    title: string | null
    secondaryActionComponent?: JSX.Element
    tooltip?: string
    component: JSX.Element
    /**
     * Opens the section whenever it changes — bumped by a section that has just added
     * something to itself (a filter, a transform), so what was added is in view.
     */
    expandRequest?: number
}

const ExpandableSection = ({
    expanded,
    title,
    secondaryActionComponent,
    tooltip,
    component,
    expandRequest,
}: Props) => {
    const theme = useTheme()
    const [isExpanded, setIsExpanded] = useState(expanded)
    useEffect(() => {
        if (expandRequest) setIsExpanded(true)
    }, [expandRequest])

    if (!title)
        return (
            <Box
                sx={{
                    backgroundColor: theme.palette.background.paper,
                    py: 1,
                    pl: 1,
                    pr: 2,
                    borderRadius: 1,
                    marginBottom: 1,
                }}
            >
                {component}
            </Box>
        )

    return (
        <Box sx={{ marginBottom: 1, borderRadius: 1, overflow: "hidden" }}>
            <ListItem
                button
                onClick={() => setIsExpanded(!isExpanded)}
                sx={{ backgroundColor: theme.palette.background.paper }}
                secondaryAction={
                    // flex, so the arrow is centred on the title instead of sitting on the
                    // baseline of the action next to it
                    <Box display="flex" alignItems="center">
                        {tooltip && tooltip.length > 0 && <InfoTooltip text={tooltip} />}
                        {secondaryActionComponent}
                        {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                    </Box>
                }
            >
                <ListItemText primary={title} />
            </ListItem>
            {isExpanded && (
                <Box
                    p={1}
                    sx={{
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    {component}
                </Box>
            )}
        </Box>
    )
}

export default ExpandableSection
